import { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth, useLoader } from '../hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Unstable_Grid2';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import * as actions from './actionCreators';
import * as layoutActions from '../layout/actionCreators';

const AppGridSkeleton = () => (
    <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(6, 1fr)' },
        gap: 2,
        p: 2,
    }}>
        {Array.from({ length: 12 }).map((_, i) => (
            <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Skeleton variant="rounded" width={64} height={64} />
                <Skeleton variant="text" width="80%" />
            </Box>
        ))}
    </Box>
);
import './PageUserInfo.css';
import * as constants from '../common/constants';
import * as JspUtil from '../common/JspUtil';
import * as tfaActions from '../tfapages/actionCreators';
import submitHiddenForm from '../common/submitHiddenForm';
import { IceIcon, PageContainer, PageHeader, UserMenu } from '../components';
import { useJspVariables, useNavigation, useAppContext, useDebugRender } from '../hooks';
import UserSelectorPopover from './UserSelectorPopover';
import CustomHelpPopover from './CustomHelpPopover';
import AppSelector from './AppSelector';

const tabsSx = {
    borderBottom: '1px solid #ddd',
    marginBottom: '15px',
    '& .MuiTabs-indicator': { backgroundColor: '#1e88e5' },
};

const tabSx = {
    textTransform: 'none',
    minWidth: 50,
    fontWeight: 600,
    fontSize: '14px',
    color: '#000000',
    '&:hover': { color: '#1e88e5', opacity: 1 },
    '&.Mui-selected': { color: '#1e88e5', fontWeight: 600 },
    '&:focus': { color: '#1e88e5' },
};

const PageUserInfo = () => {
    useDebugRender('PageUserInfo');
    const { showLoader, hideLoader } = useLoader();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const messageContainerRef = useRef(null);
    const { jspVariables, launcher, panelStyle } = useJspVariables();
    const { getLoginApp, getUiMsg, isMacOS } = useAppContext(jspVariables);
    const { toPageTfaRegister } = useNavigation();

    const { authenticatedUser, isAuthenticated, isLauncherAuth, ssoEnvironment } = useAuth();
    const globalId = useSelector(state => state.authenticateReducer.authSession.authenticatedUser?.globalId);
    const { appConfigs: userAppConfigs, pageConfig, favoriteApps: userFavoriteApps, dashboardConfig: userAppDashboardConfig } = useSelector(state => state.authenticateReducer.userDashboardConfig);

    const [displayMessage, setDisplayMessage] = useState(() => {
        return location?.state?.msg || '';
    });
    const [isMemberLogin] = useState(() => typeof window.jspMemberLogin !== 'undefined');
    const [favorites, setFavorites] = useState(() => {
        return userFavoriteApps && userFavoriteApps.length > 0 ? [...userFavoriteApps] : [];
    });
    const [activeTab, setActiveTab] = useState(() => {
        return userFavoriteApps && userFavoriteApps.length > 0 ? 'favorites' : 'all';
    });
    const [userSelectorAnchorEl, setUserSelectorAnchorEl] = useState(null);
    const [selectUserOptions, setSelectUserOptions] = useState([]);
    const [selectedApp, setSelectedApp] = useState({});
    const [showHelpPopover, setShowHelpPopover] = useState(false);
    const [helpPopoverTarget, setHelpPopoverTarget] = useState(null);
    const [helpPopoverContent, setHelpPopoverContent] = useState('');
    const [helpPopoverTitle, setHelpPopoverTitle] = useState('');
    const [appsLoaded, setAppsLoaded] = useState(false);
    const tfaSubmitTriggeredRef = useRef(false);

    useEffect(() => {
        if (isAuthenticated) {
            const isFederated = typeof window.jspImtFederated !== 'undefined' && !!window.jspImtFederated;
            const isMortgage = typeof window.jspIsMortgage !== 'undefined' && !!window.jspIsMortgage;
            actions.getUserAppConfigs(authenticatedUser.user, authenticatedUser.app, isFederated, isMortgage).then(config => {
                dispatch({ type: actions.USER_DASHBOARD_CONFIG, data: config });
            }).catch(err => console.error('getUserAppConfigs failed:', err))
            .finally(() => setAppsLoaded(true));
            dispatch(layoutActions.getAppUiMessages(authenticatedUser.app));
        }
        if (location?.state?.triggerTfaSubmit && authenticatedUser && !tfaSubmitTriggeredRef.current) {
            tfaSubmitTriggeredRef.current = true;
            submitHiddenForm('/appUserLogin', {
                appKey: isLauncherAuth ? authenticatedUser.displayApp : getLoginApp(),
                loginApp: authenticatedUser.authenticatedApp,
                nologin: 'Y',
                isTfaRegForward: 'Y',
                redirectUrl: window.location.origin + '/index.html'
            });
        }
        if (JspUtil.isValueAvailable(window.jspIsTfaRegForward)) {
            window.jspIsTfaRegForward = undefined;
            toPageTfaRegister();
        }
        if (userFavoriteApps && userFavoriteApps.length > 0) {
            setActiveTab('favorites');
        }
    }, [isAuthenticated]);

    useEffect(() => {
        const prevFavs = favorites;
        const newFavs = userFavoriteApps || [];
        const newFavsArray = newFavs.length > 0 ? [...newFavs] : [];
        let newActiveTab = activeTab;
        if (prevFavs.length === 0 && newFavs.length > 0 && activeTab === 'all') {
            newActiveTab = 'favorites';
        } else if (newFavs.length === 0 && activeTab === 'favorites') {
            newActiveTab = 'all';
        }
        setFavorites(newFavsArray);
        setActiveTab(newActiveTab);
    }, [userFavoriteApps]);

    const logoutUserToken = () => {
        showLoader('Logging out...');
        dispatch(actions.logoutUserToken(authenticatedUser.user));
    };

    const memberLogout = () => {
        showLoader('Logging out...');
        dispatch(actions.logoutUserToken(authenticatedUser.user, true));
        setTimeout(() => {
            submitHiddenForm('/memberLogin', {
                loginApp: getLoginApp(),
                secondAuth: JspUtil.isValueAvailable(window.jspSecondAuth) ? window.jspSecondAuth : undefined,
                refererDomain: JspUtil.isValueAvailable(window.jspRefererDomain) ? window.jspRefererDomain : '',
            });
        }, 1000);
    };

    const handleNavigate = (route) => {
        if (route === constants.ROUTE_PAGE_TFA_REGISTER) {
            dispatch(tfaActions.checkAndNavigateToTfaSettings());
        } else {
            navigate(route);
        }
    };

    const sortIcons = (iconList) => {
        iconList.sort((a, b) => {
            const aOrder = a.iconOrder != null ? Number(a.iconOrder) : Infinity;
            const bOrder = b.iconOrder != null ? Number(b.iconOrder) : Infinity;
            if (aOrder !== bOrder) return aOrder - bOrder;
            return (a.iconDisplayName || '').localeCompare(b.iconDisplayName || '');
        });
    };

    const getGroupConfig = (groupName) => {
        if (groupName === 'Additional Applications') {
            return { groupName: 'Additional Applications', groupSequence: 99999999, groupIcon: 'https://www.theice.com/publicdocs/images/ICE-Apps_Icon_64x64.png' };
        }
        const config = userAppDashboardConfig && Array.isArray(userAppDashboardConfig)
            ? userAppDashboardConfig.find(c => c.groupName === groupName)
            : null;
        return config || { groupName, groupSequence: 99998, groupIcon: 'https://www.theice.com/publicdocs/images/ICE-Apps_Icon_64x64.png' };
    };

    const getAppList = () => {
        const groups = {};
        let hasIws = false;
        if (userAppConfigs) {
            for (const appConfig of userAppConfigs) {
                const icon = JSON.parse(appConfig.icon);
                if (icon) {
                    if (typeof icon.hiddenFromDisplay !== 'undefined' && icon.hiddenFromDisplay) continue;
                    if (appConfig.coverAppUser) continue;
                    icon.users = appConfig.users;
                    icon.displays = appConfig.displays;
                    icon.appName = appConfig.appName;
                    icon.altAppUser = appConfig.altAppUser;
                    if (appConfig.appName && !icon.appKeyFromConfig) icon.appKeyFromConfig = appConfig.appName;
                    if (isMacOS()) {
                        if (icon.macUrl) icon.url = icon.macUrl;
                    }
                    if (icon.iws) hasIws = true;
                    const groupName = (icon.group && icon.group.trim() !== '') ? icon.group : 'Additional Applications';
                    if (!icon.iconUrl || icon.iconUrl.trim() === '') {
                        const gc = getGroupConfig(groupName);
                        icon.iconUrl = gc.groupIcon;
                    }
                    if (!groups[groupName]) groups[groupName] = [];
                    groups[groupName].push(icon);
                }
            }
        }
        const groupKeys = Object.keys(groups);
        const hasMultipleGroups = groupKeys.length > 1 || (groupKeys.length === 1 && groupKeys[0] !== 'Additional Applications');
        const orderedGroups = groupKeys.sort((groupA, groupB) => {
            if (groupA === 'Additional Applications') return 1;
            if (groupB === 'Additional Applications') return -1;
            const configA = getGroupConfig(groupA);
            const configB = getGroupConfig(groupB);
            const hasSeqA = configA.groupSequence !== 99998;
            const hasSeqB = configB.groupSequence !== 99998;
            if (hasSeqA && !hasSeqB) return -1;
            if (!hasSeqA && hasSeqB) return 1;
            if (hasSeqA && hasSeqB) return configA.groupSequence - configB.groupSequence;
            if (!hasSeqA && !hasSeqB) return groupA.localeCompare(groupB);
            return 0;
        }).map(groupName => {
            const groupApps = groups[groupName];
            sortIcons(groupApps);
            const groupConfig = getGroupConfig(groupName);
            return { groupName, apps: groupApps, showGroupHeader: hasMultipleGroups || groupName !== 'Additional Applications', groupSequence: groupConfig.groupSequence, groupIcon: groupConfig.groupIcon };
        });
        return { groups: orderedGroups, hasIws };
    };

    const appList = useMemo(() => getAppList(), [userAppConfigs, userAppDashboardConfig, isMacOS]);

    const onIconClick = (event, app) => {
        gotoApplication(event, app);
    };

    const gotoApplication = async (event, app) => {
        if (app.conUrl) dispatch(layoutActions.testIconConnection(app.conUrl));
        if (app.users) {
            if (app.users.length > 1) {
                if (app.altAppUser) {
                    const result = await actions.gotoAppUrl(app, app.noCredential, app.altAppUser, app.altApp, isMemberLogin, globalId);
                    if (result.sessionExpired) dispatch({ type: actions.SESSION_EXPIRED_DIALOG, data: true });
                    return;
                }
                // Multiple users — show selector popover
                const selections = app.users.map(userId => ({ l: app.displays[userId], v: userId }));
                setSelectUserOptions(selections);
                setSelectedApp(app);
                setUserSelectorAnchorEl(event.currentTarget);
                return;
            }
            const result = await actions.gotoAppUrl(app, app.noCredential, app.users[0], app.appName, isMemberLogin, globalId);
            if (result.sessionExpired) dispatch({ type: actions.SESSION_EXPIRED_DIALOG, data: true });
        }
    };

    const gotoAppWithUser = (user) => {
        setUserSelectorAnchorEl(null);
        actions.gotoAppWithUser(selectedApp, user, selectedApp.appName, globalId);
    };

    const toggleFavorite = (appIdentifier) => {
        const favs = favorites ? [...favorites] : [];
        const idx = favs.indexOf(appIdentifier);
        const wasEmpty = favs.length === 0;
        if (idx === -1) favs.push(appIdentifier);
        else favs.splice(idx, 1);
        let newActiveTab = activeTab;
        if (wasEmpty && favs.length > 0) newActiveTab = 'favorites';
        else if (favs.length === 0 && activeTab === 'favorites') newActiveTab = 'all';
        setFavorites(favs);
        setActiveTab(newActiveTab);
        actions.updateUserFavorites(authenticatedUser.user, authenticatedUser.app, favs);
    };

    const handleHelpToggle = (event, helpNote, helpTitle) => {
        if (showHelpPopover && helpPopoverTarget === event.target) {
            setShowHelpPopover(false); setHelpPopoverTarget(null); setHelpPopoverContent(''); setHelpPopoverTitle('');
        } else {
            setShowHelpPopover(true); setHelpPopoverTarget(event.target); setHelpPopoverContent(helpNote); setHelpPopoverTitle(helpTitle);
        }
    };

    const userInfoAppsInstructions = getUiMsg(constants.USER_INFO_APPS_INSTRUCTIONS);

    return (
        <PageContainer id="pageUserInfo" dataAutomationId="page_user_info" md={8}>
            <Box style={panelStyle} sx={{ minHeight: '570px', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                    {authenticatedUser && authenticatedUser.displayUser && (
                        <Typography component="span" sx={{ fontSize: 'smaller', color: '#494949', lineHeight: '1.8em' }}>
                            {authenticatedUser.displayUser}
                        </Typography>
                    )}
                    <UserMenu
                        authenticatedUser={authenticatedUser}
                        isMemberLogin={isMemberLogin}
                        onLogout={logoutUserToken}
                        onMemberLogout={memberLogout}
                        onNavigate={handleNavigate}
                    />
                </Box>
                <IceIcon launcher={launcher} />
                <PageHeader sx={{ whiteSpace: 'nowrap'}} dataAutomationId="user_info_header">{JspUtil.getLabelFromJspVariables('title_application_dashboard')}</PageHeader>

                <Box sx={{ my: 1 }}>
                    <Typography component="span" sx={{ fontSize: 'small' }}>
                        {userInfoAppsInstructions ? userInfoAppsInstructions : 'All ICE Applications configured for your Global ID/User ID can be accessed via the dashboard'}
                    </Typography>
                    {pageConfig.title &&
                        <Box dangerouslySetInnerHTML={{ __html: pageConfig.title }} />
                    }
                </Box>
                <Grid container direction="column" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Grid xs={12}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, newValue) => setActiveTab(newValue)}
                            variant="scrollable"
                            scrollButtons="auto"
                            aria-label="application tabs"
                            sx={tabsSx}
                            data-automation-id="user_info_tabs"
                        >
                            <Tab label="Favorites" value="favorites" sx={tabSx} data-automation-id="user_info_tab_favorites" />
                            <Tab label="All Applications" value="all" sx={tabSx} data-automation-id="user_info_tab_all" />
                            {appList.groups.filter(g => g.groupName !== 'Additional Applications').map(g => (
                                <Tab key={g.groupName} label={g.groupName} value={g.groupName} sx={tabSx}
                                    data-automation-id={`user_info_tab_${String(g.groupName).replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase()}`} />
                            ))}
                        </Tabs>
                    </Grid>

                    {pageConfig.iwsHelp && appList.hasIws && (
                        <Grid xs={12}>
                            <Box sx={{ fontSize: 'x-small', lineHeight: '1.4' }} dangerouslySetInnerHTML={{ __html: pageConfig.iwsHelp }} />
                        </Grid>
                    )}

                    <Grid xs={12} sx={{ flex: 1 }}>
                        <Box>
                            {!appsLoaded ?
                                <AppGridSkeleton />
                                :
                                <>
                                    {activeTab === 'favorites' && (
                                        <Box id="favorites" data-automation-id="favorites">
                                            {(favorites && favorites.length > 0) ?
                                                appList.groups.map(g => {
                                                    const favApps = g.apps.filter(x => {
                                                        return x.app && favorites.indexOf(x.app) !== -1;
                                                    });
                                                    if (favApps.length === 0) return null;
                                                    sortIcons(favApps);
                                                    return (
                                                        <Box key={'fav-' + g.groupName}>
                                                            {g.showGroupHeader && (
                                                                <Box sx={{ color: '#1e88e5', fontSize: '14px', fontWeight: '600' }}>{g.groupName}</Box>
                                                            )}
                                                            <AppSelector apps={favApps} favorites={favorites} onIconClick={onIconClick} toggleFavorite={toggleFavorite} handleHelpToggle={handleHelpToggle} />
                                                        </Box>
                                                    );
                                                })
                                                :
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    py: 6,
                                                    gap: 1.5,
                                                }} data-automation-id="no_favorites_empty">
                                                    <StarBorderIcon sx={{ fontSize: 64, color: '#bdbdbd' }} />
                                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                                                        {JspUtil.getLabelFromJspVariables('label_no_favorites')}
                                                    </Typography>
                                                </Box>
                                            }
                                        </Box>
                                    )}
                                    {appList.groups.filter(g => g.groupName !== 'Additional Applications').map(g => (
                                        activeTab === g.groupName && (
                                            <Box key={g.groupName} id={g.groupName} data-automation-id={g.groupName}>
                                                <AppSelector apps={g.apps} favorites={favorites} onIconClick={onIconClick} toggleFavorite={toggleFavorite} handleHelpToggle={handleHelpToggle} />
                                            </Box>
                                        )
                                    ))}
                                    {activeTab === 'all' && (
                                        <Box id="all" data-automation-id="all">
                                            {appList.groups.map(g => (
                                                <Box key={g.groupName}>
                                                    {g.showGroupHeader && (
                                                        <Box sx={{ color: '#1e88e5', fontSize: '14px', fontWeight: '600' }}>{g.groupName}</Box>
                                                    )}
                                                    <AppSelector apps={g.apps} favorites={favorites} onIconClick={onIconClick} toggleFavorite={toggleFavorite} handleHelpToggle={handleHelpToggle} />
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </>
                            }
                        </Box>
                    </Grid>

                    <Grid xs={12}>
                        <Popover
                            open={Boolean(displayMessage)}
                            onClose={() => setDisplayMessage('')}
                            anchorEl={messageContainerRef.current}
                            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        >
                            <Typography sx={{ margin: '8px 14px' }}>{displayMessage}</Typography>
                        </Popover>
                        <Popover
                            open={Boolean(userSelectorAnchorEl) && selectUserOptions.length > 0}
                            onClose={() => setUserSelectorAnchorEl(null)}
                            anchorEl={userSelectorAnchorEl}
                        >
                            <UserSelectorPopover users={selectUserOptions} cb={(selectedUser) => gotoAppWithUser(selectedUser)} />
                        </Popover>
                        <Popover
                            open={showHelpPopover}
                            onClose={() => { setShowHelpPopover(false); setHelpPopoverTarget(null); setHelpPopoverContent(''); setHelpPopoverTitle(''); }}
                            anchorEl={helpPopoverTarget}
                            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        >
                            <CustomHelpPopover content={helpPopoverContent} title={helpPopoverTitle} />
                        </Popover>
                    </Grid>
                    <Grid xs={12} sx={{ mt: 'auto', pt: 2 }}>
                        {pageConfig.appHelp &&
                            <Box dangerouslySetInnerHTML={{ __html: pageConfig.appHelp }} />
                        }
                        {pageConfig.appContact &&
                            <Box dangerouslySetInnerHTML={{ __html: pageConfig.appContact }} />
                        }
                        <Typography variant="caption" sx={{ fontSize: "7px", color: "#797676" }}>SSO Version:{ssoEnvironment.version}</Typography>
                        {ssoEnvironment && (ssoEnvironment.isProd === 'false') &&
                            <Typography variant="caption" sx={{ color: '#b00017', fontSize: "8px", display: 'block' }}>Env:{ssoEnvironment.env}</Typography>
                        }
                    </Grid>
                </Grid>
            </Box>
        </PageContainer>
    );
};

export default PageUserInfo;
